import React from 'react';
import './Pricing.css';

const Pricing = () => {
  const openWhatsApp = (message = '') => {
    const phoneNumber = '+256703521101';
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
  };

  const pricingPlans = {
    online: {
      title: "Online Subscription",
      price: "UGX 300,000",
      priceDesc: "initial setup fee",
      dailyPricing: {
        title: "Daily Active User Pricing (based on total active users per day):",
        items: [
          "1-12 active employees: UGX 500 per employee/day",
          "13-22 active employees: UGX 400 per employee/day",
          "23+ active employees: UGX 300 per employee/day"
        ],
        note: "The system counts your total active employees each day and applies one flat rate to all employees based on that total count. For example, if you have 15 active employees on Tuesday, all 15 are charged at UGX 400 each for that day."
      },
      otherFeatures: [
        {
          title: "Flexible Payment System:",
          description: "Load any amount you want into your Clinic Pro account (like mobile money airtime). The system automatically deducts daily usage fees from your balance. You'll receive low balance alerts, but if your balance reaches zero, the system will pause until you top up to ensure you never incur unexpected charges."
        },
        {
          title: "AI Features:",
          description: "Currently included at no extra cost"
        },
        {
          title: "Messaging:",
          description: "SMS (UGX 100-150) and WhatsApp (UGX 300) charged per message"
        }
      ],
      benefits: {
        title: "Why Choose Online?",
        items: [
          "No upfront hardware costs - save millions compared to offline installation",
          "Automatic updates - always have the latest features without extra charges",
          "Power outage protection - keeps working during blackouts (unlike offline systems) because it runs in the cloud",
          "Continuous data processing - AI sorting and background tasks continue even when your clinic's power is off",
          "Access from anywhere - use on mobile or desktop, inside or outside the clinic",
          "Pay only for what you use - perfect for clinics with varying staff numbers"
        ]
      },
      ctaText: "Get Started Online",
      ctaMessage: "I'm interested in the Online Subscription for Clinic Pro. Can you tell me more?"
    },
    offline: {
      title: "Offline Installation",
      prices: [
        { value: "UGX 3,000,000", desc: "one-time installation fee" },
        { value: "UGX 800,000", desc: "annual maintenance fee" }
      ],
      features: [
        "Complete on-premises installation on your private servers",
        "Technical wiring and connection of all facility computers",
        "Unlimited users - one flat rate regardless of facility size",
        "Messaging: SMS (UGX 100-150) and WhatsApp (UGX 300) charged per message",
        "Data Ownership: Complete control over all your data with no external cloud storage",
        "Customization: Greater potential for system customization to your exact needs"
      ],
      considerations: {
        title: "Considerations for Offline:",
        items: [
          "Higher upfront cost - requires significant initial investment",
          "Vulnerable to power outages - system goes down when your power fails",
          "Manual updates required - may incur additional costs for major upgrades",
          "Fixed location - can only be accessed from connected clinic computers",
          "Annual maintenance fee - required regardless of your usage levels"
        ]
      },
      suitability: {
        title: "Who Should Choose Offline?",
        items: [
          "Large facilities with very consistent, high daily usage",
          "Clinics with reliable power and IT infrastructure",
          "Organizations with strict data residency requirements",
          "Facilities needing deep system customization",
          "Clinics in areas with unreliable internet connectivity"
        ]
      },
      implementation: "Our offline installation includes a site assessment by technicians, hardware setup and configuration, network integration of all workstations, comprehensive staff training, and ongoing support - all completed within a streamlined implementation timeline.",
      ctaText: "Request Offline Installation",
      ctaMessage: "I'm interested in Offline Installation for Clinic Pro. Can you provide more details?"
    }
  };

  const valueProps = [
    {
      title: "Fair Pricing",
      description: "Our tiered pricing ensures larger facilities get better rates, while our active-user model means you never pay for unused licenses."
    },
    {
      title: "Professional Support",
      description: "Our team is quick to respond and always available when you need us. We pride ourselves on professional, timely service."
    },
    {
      title: "Cutting-Edge Features",
      description: "Included AI capabilities and automated messaging put Clinic Pro ahead of competitors, all at a fraction of the cost."
    },
    {
      title: "Flexible Options",
      description: "Choose the deployment method that works best for your facility - cloud-based or on-premises."
    }
  ];

  return (
    <div className="pricing-container">
      <header className="pricing-header">
        <h1>Clinic Pro Pricing</h1>
        <p className="subtitle">Professional, Affordable Clinic Management Solutions</p>
        <p className="intro">
          At Clinic Pro, we believe in fair, transparent pricing that scales with your facility size. 
          Our innovative pay-per-use model ensures you only pay for what you actually use, making 
          us the most cost-effective solution in the market.
        </p>
      </header>

      <div className="pricing-options">
        {/* Online Subscription Card */}
        <div className="pricing-card online">
  <div className="card-content">
    <h2>{pricingPlans.online.title}</h2>
    <div className="price">
      <span className="initial-fee">{pricingPlans.online.price}</span>
      <span className="price-desc">{pricingPlans.online.priceDesc}</span>
    </div>
    
            
            {/* Daily Pricing Section */}
            <div className="daily-pricing-section">
      <h3 className="daily-pricing-title">{pricingPlans.online.dailyPricing.title}</h3>
      <ul className="tiered-pricing">
        {pricingPlans.online.dailyPricing.items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
      <p className="note"><strong>Important:</strong> {pricingPlans.online.dailyPricing.note}</p>
            </div>
            
            {/* Other Features */}
            <div className="other-features">
      {pricingPlans.online.otherFeatures.map((feature, index) => (
        <div key={index} className="feature-block">
          <h3 className="feature-title">{feature.title}</h3>
          <p className="feature-description">{feature.description}</p>
        </div>
      ))}
    </div>
            
    <div className="benefits">
      <h3>{pricingPlans.online.benefits.title}</h3>
      <ul>
        {pricingPlans.online.benefits.items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    </div>
  </div>
          <div className="card-footer">
            <button 
              className="cta-button" 
              onClick={() => openWhatsApp(pricingPlans.online.ctaMessage)}
            >
              {pricingPlans.online.ctaText}
            </button>
          </div>
        </div>

        {/* Offline Installation Card */}
        <div className="pricing-card offline">
          <div className="card-content">
            <h2>{pricingPlans.offline.title}</h2>
            
            {pricingPlans.offline.prices.map((price, index) => (
              <div className="price" key={index}>
                <span className="initial-fee">{price.value}</span>
                <span className="price-desc">{price.desc}</span>
              </div>
            ))}
            
            <ul className="features">
              {pricingPlans.offline.features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
            
            <div className="benefits">
              <h3>{pricingPlans.offline.considerations.title}</h3>
              <ul>
                {pricingPlans.offline.considerations.items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              
              <h3>{pricingPlans.offline.suitability.title}</h3>
              <ul>
                {pricingPlans.offline.suitability.items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="card-footer">
            <button 
              className="cta-button" 
              onClick={() => openWhatsApp(pricingPlans.offline.ctaMessage)}
            >
              {pricingPlans.offline.ctaText}
            </button>
          </div>
        </div>
      </div>

      <div className="value-proposition">
        <h2>Why Clinic Pro Stands Out</h2>
        <div className="value-cards">
          {valueProps.map((prop, index) => (
            <div className="value-card" key={index}>
              <h3>{prop.title}</h3>
              <p>{prop.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="contact-cta">
        <h2>Ready to Transform Your Clinic Management?</h2>
        <p>Contact our team today for a personalized consultation and see how Clinic Pro can work for your specific needs.</p>
        <button 
          className="contact-button" 
          onClick={() => openWhatsApp("I'd like to learn more about Clinic Pro. Can we schedule a consultation?")}
        >
          Contact Sales
        </button>
      </div>
    </div>
  );
};

export default Pricing;